import React from 'react';
import { Card, CardContent, CardHeader, Typography, IconButton, Tooltip, Button } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import GridOnIcon from '@mui/icons-material/GridOn';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

type SortableCardProps = {
  customButton?: React.ReactNode;
  title: string;
  onSort?: (sortOption: 'ascending' | 'descending') => void;
  children: React.ReactNode;
  showTile?: boolean;
  showSort?: boolean;
  onTile?: () => void;
  tile?: boolean;
  showAddButton?: boolean;
  showApplyButton?: boolean;
  onApply?: () => void;
  onAdd?: () => void;
  onDelete?: () => void;
  showDeleteButton?: boolean;
  applyDisabled?: boolean;
};

const SortableCard: React.FC<SortableCardProps> = ({ title, showSort = true, onSort, children, onTile, tile, showTile, 
                                                      showAddButton, showApplyButton, onApply, onAdd, 
                                                      applyDisabled, customButton, showDeleteButton, onDelete}) => {
  const handleSortAscending = () => {
    if (onSort) {
      onSort('ascending');
    }
  };

  const handleSortDescending = () => {
    if (onSort) {
      onSort('descending');
    }
  };

  const iconStyles = {
    color: 'grey', // this sets the icon color to grey
    fontSize: 'small' // this sets the icon to a predefined small size
  };

  const tileIconColor = tile ? 'blue' : 'grey';
  const darkGrey = '#131313'

  return (
    <Card elevation={0}>
      <CardHeader
        title={<Typography component={"h2"} variant="subtitle1" sx={{ color: darkGrey }}>{title}</Typography>}
        action={
          <>
         {customButton && customButton}
         {showApplyButton &&
            <Button 
              variant="outlined" 
              size='small' 
              disabled={applyDisabled}
              sx={{
                marginRight: '5px',
                minWidth: '20px',
                color: applyDisabled ? 'grey' : 'white', 
                border: applyDisabled ? '1px solid grey' : '1px solid darkgreen',
                bgcolor: applyDisabled ? 'inherit' : 'green',
                paddingLeft: '5px',
                paddingRight: '5px',
                fontSize: '0.55rem',
                '& .MuiButton-label': {
                  lineHeight: '1.5',
                },
                '&:hover': {
                  bgcolor: applyDisabled ? '#f8f9fa' : 'darkgreen',
                  border: applyDisabled ? '1px solid grey' : '1px solid darkgreen',
                },
              }} 
              onClick={onApply}
              >
              {true ? 'Apply' : 'Save'} 
            </Button>
          }
          {showDeleteButton &&
            <IconButton aria-label="Delete" onClick={onDelete}>
              <RemoveCircleOutlineIcon sx={iconStyles}/>
            </IconButton>
          }
          {showAddButton &&
            <IconButton aria-label="Add" onClick={onAdd}>
              <AddCircleOutlineOutlinedIcon sx={iconStyles}/>
            </IconButton>
          }
          {showSort &&
            <>
            <Tooltip title="Sort Ascending">
              <IconButton onClick={handleSortAscending}>
                <ArrowUpwardIcon sx={iconStyles} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sort Descending">
              <IconButton onClick={handleSortDescending}>
                <ArrowDownwardIcon sx={iconStyles} />
              </IconButton>
            </Tooltip>
            </>
          }
          {showTile && 
            <Tooltip title="Toggle Tile View">
              <IconButton aria-label="Toggle tile view" onClick={onTile}>
                <GridOnIcon sx={{ ...iconStyles, color: tileIconColor }} />
              </IconButton>
            </Tooltip>
          }
          </>
        }
        titleTypographyProps={{ variant: 'body2', color: 'textSecondary' }}
        sx={{ paddingBottom: .3, position: 'relative', paddingTop: .8, paddingRight: 2.5}}
      />
      <CardContent sx={{ paddingTop: '0px', paddingBottom: '0px' }}>
        {children}
      </CardContent>
    </Card>
  );
};

export default SortableCard;
